import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppInitService } from 'src/app/app-init.service';
import { AppConfigurations } from 'src/app/shared/models/appConfiguration.model';
import { AuthenticationType } from '../models/authentication-type.enum';
import { CertificateValidate } from '../models/certificate-validate';
import { DomainAndExternalSchemes } from '../models/domain-external-scheme';
import { DomainRequest } from '../models/domain-request';
import { EnableTwoFactorAuthForTenant } from '../models/enable-twofactor-auth';
import { ExternalScheme } from '../models/external-echeme';
import { IdpMetadata } from '../models/idp-metadata';
import { JitDomainList } from '../models/jit-domain-list';
import { JitProvisionTenantConfiguration } from '../models/jit-provision-tenant-config';
import { PasswordExpirationPolicyForTenant } from '../models/password-expiration-policy';
import { SchemeType } from "../models/scheme-type.enum";
import { TenantConfiguration } from '../models/tenant-configuration';

declare const window: any;

@Injectable({
  providedIn: 'root'
})
export class IdentityServiceService {

  private baseUrl: string = '';
  private apiController: string = '';
  private schemeApiController: string = '';
  private domainApiController: string = '';
  private appConfig!: AppConfigurations;
  private domainOwnerApiController: string = '';

  constructor(private http: HttpClient, private appInitService: AppInitService) {
    appInitService.getAppConfiguration$().subscribe({
      next: (config) => this._onReceiveAppConfig(config)
    })
  }

  private _onReceiveAppConfig(appConfig: AppConfigurations) {
    this.appConfig = appConfig;
    this.baseUrl = this.appConfig.identityServerApi.baseApiUrl;
    this.apiController = this.appConfig.identityServerApi.tenantController;
    this.schemeApiController = this.appConfig.identityServerApi.schemeController;
    this.domainApiController = this.appConfig.identityServerApi.domainController;
    this.domainOwnerApiController = this.appConfig.identityServerApi.domainOwnerController;
  }
  public enableTwoFactorAuthentication(twoFactorData: EnableTwoFactorAuthForTenant): Observable<boolean> {
    return this.http.patch<boolean>(`${this.baseUrl}${this.apiController}/enabletwofactor`, twoFactorData)
      .pipe(
        catchError(this.handleError<boolean>('enableTwoFactorAuthentication', false))
      );
  }

  public enforcePasswordExpirationPolicy(passwardPolicy: PasswordExpirationPolicyForTenant): Observable<boolean> {
    return this.http.patch<boolean>(`${this.baseUrl}${this.apiController}/enforcepasswordexpirationpolicy`, passwardPolicy)
      .pipe(
        catchError(this.handleError<boolean>('enforcePasswordExpirationPolicy', false))
      );
  }

  public updateAuthenticationType(authenticationType: AuthenticationType): Observable<boolean> {
    return this.http.patch<boolean>(`${this.baseUrl}${this.apiController}/updateauthenticationtype/${authenticationType.valueOf()}`, {})
      .pipe(
        catchError(this.handleError<boolean>('updateAuthenticationType', false))
      );
  }

  public getSystemConfiguration(): Observable<TenantConfiguration> {
    return this.http.get<TenantConfiguration>(`${this.baseUrl}${this.apiController}/configuration`)
      .pipe(
        catchError(this.handleError<TenantConfiguration>('getSystemConfiguration'))
      );
  }

  public getDomains(): Observable<DomainAndExternalSchemes[]> {
    return this.http.get<DomainAndExternalSchemes[]>(`${this.baseUrl}${this.schemeApiController}/domaindetails`)
      .pipe(
        catchError(this.handleError<DomainAndExternalSchemes[]>('getDomains', []))
      );
  }

  public domainNameExists(domainName: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.baseUrl}${this.domainApiController}/domainNameExists?domainName=${domainName}`)
      .pipe(
        catchError(this.handleError<boolean>('domainNameExists', false))
      );
  }

  public createDomain(domainRequest: DomainRequest): Observable<number> {
    return this.http.post<number>(`${this.baseUrl}${this.domainApiController}`, domainRequest)
      .pipe(
        catchError(this.handleError<number>('createDomain', 0))
      );
  }

  public editDomain(domainRequest: DomainRequest): Observable<boolean> {
    return this.http.patch<boolean>(`${this.baseUrl}${this.domainApiController}`, domainRequest)
      .pipe(
        catchError(this.handleError<boolean>('editDomain', false))
      );
  }

  public DeleteDomain(domainId: number): Observable<boolean> {
    return this.http.delete<boolean>(`${this.baseUrl}${this.domainApiController}/${domainId}`)
      .pipe(
        catchError(this.handleError<boolean>('getDomains', false))
      );
  }

  public createExternalScheme(externalScheme: ExternalScheme): Observable<string> {
    console.log(externalScheme);
    return this.http.post<string>(`${this.baseUrl}${this.schemeApiController}`, externalScheme)
      .pipe(
        catchError(this.handleError<string>('createExternalScheme', ''))
      );
  }

  public validateCertificate(file: File): Observable<CertificateValidate> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<CertificateValidate>(`${this.baseUrl}${this.schemeApiController}/validatecertificate`, formData)
      .pipe(
        catchError(this.handleError<CertificateValidate>('validateCertificate'))
      );
  }

  public getCertificateDetails(domainId: number): Observable<CertificateValidate> {
    return this.http.get<CertificateValidate>(`${this.baseUrl}${this.schemeApiController}/certificateDetails/${domainId}`)
      .pipe(
        catchError(this.handleError<CertificateValidate>('certificateDetails'))
      );
  }

  public getAssertionConsumerEndpoint(domainId: number, schemeType: SchemeType): Observable<IdpMetadata> {
    return this.http.get<IdpMetadata>(`${this.baseUrl}${this.schemeApiController}/assertionconsumerendpoint/domain/${domainId}/scheme/${schemeType.valueOf()}`)
      .pipe(
        catchError(this.handleError<IdpMetadata>('getAssertionConsumerEndpoint'))
      );
  }

  public getIdentityServerUrl(): string {
    return this.appConfig.identityServerApi.baseApiUrl;
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      return of(result as T);
    };
  }

  public updateJitProvision(tenantConfiguration: JitProvisionTenantConfiguration): Observable<boolean> {
    return this.http.patch<boolean>(`${this.baseUrl}${this.apiController}/jitProvision`, tenantConfiguration)
      .pipe(
        catchError(this.handleError<boolean>('updateJitProvision', false))
      );
  }

  public editDomainOwner(jitDomainList: JitDomainList): Observable<boolean> {
    return this.http.patch<boolean>(`${this.baseUrl}${this.domainOwnerApiController}/editDomainJit`, jitDomainList)
      .pipe(
        catchError(this.handleError<boolean>('editDomainJit', false))
      );
  }

}
